import { useState, useMemo } from "react";
import { Tooltip } from "antd";
import { CheckSquareOutlined } from "@ant-design/icons";
import { fetchTasks } from "../../../services/customer360Service";
import {
  cardStyle, num, dateTime, dateOnly, chip, TONE, titleCase,
  PanelHeader, PanelTable, PanelSkeleton, PanelError, usePanelData,
} from "./shared";

/**
 * Tasks raised inside the customer's own workspace.
 *
 * `status_id` and `priority_id` arrive as numbers with no master to resolve them
 * against, so they are labelled where the mapping is known and shown as the raw
 * id where it is not. Inventing names for ids would be worse than admitting the
 * id — a wrong label is read as fact.
 */

const PRIORITY = {
  1: { label: "Low", tone: TONE.slate },
  2: { label: "Normal", tone: TONE.blue },
  3: { label: "High", tone: TONE.amber },
  4: { label: "Urgent", tone: TONE.red },
};

const STATUS = {
  1: { label: "Open", tone: TONE.blue },
  2: { label: "In progress", tone: TONE.amber },
  3: { label: "Completed", tone: TONE.green },
  4: { label: "Cancelled", tone: TONE.slate },
};

const byId = (map, id) => {
  const hit = map[Number(id)];
  return hit ? chip(hit.label, hit.tone) : chip(`#${id ?? "—"}`, TONE.slate);
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "done", label: "Completed" },
];

const TasksTab = ({ userId, active }) => {
  const p = usePanelData(fetchTasks, userId, active);
  const [filter, setFilter] = useState("all");

  /* Filtered on what is loaded, not on the server. The chips narrow the page in
     front of you; the pager and its count stay the account's real totals rather
     than pretending the filter reached the whole set. */
  const rows = useMemo(() => {
    if (filter === "all") return p.rows;
    const done = filter === "done";
    return (p.rows || []).filter((r) => Boolean(r.completed_at) === done);
  }, [p.rows, filter]);

  if (p.loading && !p.data) return <PanelSkeleton />;
  if (p.error) return <PanelError error={p.error} onRetry={p.reload} />;

  const columns = [
    {
      title: "Task", dataIndex: "title", width: 280,
      render: (v) => (
        <Tooltip title={v}>
          <span style={{ fontWeight: 600, color: "#0f172a", fontSize: 12.5 }}>{v || "—"}</span>
        </Tooltip>
      ),
    },
    { title: "Status", dataIndex: "status_id", width: 120, render: (v) => byId(STATUS, v) },
    { title: "Priority", dataIndex: "priority_id", width: 110, render: (v) => byId(PRIORITY, v) },
    {
      title: "TM number", dataIndex: "tm_number", width: 125,
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", fontWeight: 600 }}>{v || "—"}</span>,
    },
    {
      title: "Type", dataIndex: "task_type", width: 150,
      render: (v) => (v ? chip(titleCase(v), TONE.violet) : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Source", dataIndex: "source", width: 120,
      render: (v) => <span style={{ fontSize: 12.5, color: "#64748b" }}>{titleCase(v)}</span>,
    },
    {
      title: "Created by", dataIndex: "created_by_name", width: 150,
      render: (v) => <span style={{ fontSize: 12.5, color: "#334155" }}>{v || "—"}</span>,
    },
    {
      title: "Assigned to", dataIndex: "assigned_to_name", width: 150,
      render: (v) => (v
        ? <span style={{ fontSize: 12.5, color: "#334155", fontWeight: 500 }}>{v}</span>
        : chip("Unassigned", TONE.slate)),
    },
    {
      title: "Due", dataIndex: "due_date", width: 125,
      /* Overdue only while it is still open — a task finished late is finished,
         and flagging it red forever tells the reader to act on nothing. */
      render: (v, r) => {
        if (!v) return <span style={{ color: "#cbd5e1" }}>—</span>;
        const late = !r.completed_at && new Date(v) < new Date();
        return (
          <span style={{ fontSize: 12.5, whiteSpace: "nowrap", color: late ? "#dc2626" : "#475569", fontWeight: late ? 700 : 400 }}>
            {dateOnly(v)}
          </span>
        );
      },
    },
    {
      title: "Completed", dataIndex: "completed_at", width: 165,
      render: (v) => (v
        ? <Tooltip title={dateTime(v)}>{chip("Done", TONE.green)}</Tooltip>
        : chip("Open", TONE.amber)),
    },
    {
      title: "Created", dataIndex: "created_at", width: 165,
      render: (v) => <span style={{ fontSize: 12.5, color: "#94a3b8", whiteSpace: "nowrap" }}>{dateTime(v)}</span>,
    },
  ];

  return (
    <div style={cardStyle}>
      <PanelHeader
        icon={<CheckSquareOutlined />}
        title="Tasks"
        subtitle="Raised inside the customer's workspace"
        iconBg="linear-gradient(135deg,#0d9488,#14b8a6)"
        right={(
          <div style={{ display: "flex", gap: 6 }}>
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                style={{
                  padding: "4px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                  cursor: "pointer", whiteSpace: "nowrap",
                  border: `1px solid ${filter === f.key ? "#99f6e4" : "#e2e8f0"}`,
                  background: filter === f.key ? "#f0fdfa" : "#fff",
                  color: filter === f.key ? "#0d9488" : "#64748b",
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      />
      {filter !== "all" && (
        <div style={{ padding: "8px 22px", fontSize: 12, color: "#94a3b8", borderBottom: "1px solid #f8fafc" }}>
          Showing {num(rows.length)} of {num(p.rows.length)} on this page — the filter applies to loaded rows.
        </div>
      )}
      <PanelTable
        columns={columns}
        rows={rows}
        loading={p.loading}
        page={p.page}
        total={p.total}
        perPage={p.perPage}
        onPage={p.goToPage}
        empty="No tasks on this account yet."
        scrollX={1660}
      />
    </div>
  );
};

export default TasksTab;
